package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.ast.ThemeFileAst
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticCollector
import top.wcpe.allincore.dsl.compiler.diag.DslError

/**
 * 主题继承 Pass：把 `theme { extends = "<父主题>" ... }` 引用的父主题 token 作为 baseline
 * flatten 进子主题（子字段覆盖父）。
 *
 * 运行时机：[ThemeCollectorPass] 之后、[DerivativeThemePass] 之前——使父主题的种子/字段先并入
 * 子主题，再由派生对完整 token 统一补全。
 *
 * - 无 extends 的主题零开销跳过。
 * - 父主题未注册报 E795；继承链存在循环报 E794。两种错误下以主题自身 token 兜底，不中断编译。
 * - flatten 后移除 extends 元标记，避免重复解析。
 */
class ThemeInheritancePass(
    private val registry: ThemeRegistry,
    private val diag: DiagnosticCollector,
) {

    fun flatten(themeAsts: List<ThemeFileAst>) {
        val positionOf = themeAsts.associate { it.schemeName to it.position }
        // 无任何 extends 时零开销返回
        val anyExtends = registry.schemes()
            .any { (registry.lookup(it)?.get("extends") as? String)?.isNotBlank() == true }
        if (!anyExtends) return

        val resolved = HashMap<String, LinkedHashMap<String, Any>>()
        val resolvedNodes = HashMap<String, LinkedHashMap<String, top.wcpe.allincore.dsl.compiler.ast.ThemeTokenAst>>()
        val visiting = LinkedHashSet<String>()

        fun resolve(scheme: String): LinkedHashMap<String, Any>? {
            resolved[scheme]?.let { return it }
            val own = registry.lookup(scheme) ?: return null
            val ownNodes = registry.tokenNodes(scheme)
            // 循环：scheme 已在当前解析栈上
            if (scheme in visiting) {
                val position = ownNodes["extends"]?.valuePosition ?: positionOf.getValue(scheme)
                diag.error(DslError.E794_THEME_EXTENDS_CYCLE, position, scheme)
                return LinkedHashMap(own)
            }
            val parent = (own["extends"] as? String)?.takeIf { it.isNotBlank() }
            if (parent == null) {
                val copy = LinkedHashMap(own)
                resolved[scheme] = copy
                resolvedNodes[scheme] = LinkedHashMap(ownNodes)
                return copy
            }
            if (registry.lookup(parent) == null) {
                val position = ownNodes["extends"]?.valuePosition ?: positionOf.getValue(scheme)
                diag.error(DslError.E795_THEME_EXTENDS_UNRESOLVED, position, scheme, parent)
                val copy = LinkedHashMap(own).apply { remove("extends") }
                resolved[scheme] = copy
                resolvedNodes[scheme] = LinkedHashMap(ownNodes).apply { remove("extends") }
                return copy
            }
            visiting.add(scheme)
            val parentResolved = resolve(parent)
            visiting.remove(scheme)
            val merged = LinkedHashMap<String, Any>()
            val mergedNodes = LinkedHashMap<String, top.wcpe.allincore.dsl.compiler.ast.ThemeTokenAst>()
            if (parentResolved != null) {
                merged.putAll(parentResolved)
                resolvedNodes[parent]?.let(mergedNodes::putAll)
            }
            merged.putAll(own)            // 子字段覆盖父
            mergedNodes.putAll(ownNodes)  // 覆盖时同步采用子定义位置
            merged.remove("extends")      // flatten 后移除继承标记
            mergedNodes.remove("extends")
            resolved[scheme] = merged
            resolvedNodes[scheme] = mergedNodes
            return merged
        }

        for (scheme in registry.schemes().toList()) resolve(scheme)
        for ((scheme, tokens) in resolved) {
            registry.registerWithNodes(scheme, tokens, resolvedNodes[scheme].orEmpty())
        }
    }
}
